export const FRONTEND_SWIM_LANE_HEADER = 'X-FE-Swim-Lane';
export const BACKEND_SWIM_LANE_HEADER = 'X-Swim-Lane';
export const FRONTEND_SWIM_LANE_QUERY_PARAM = 'feSwimLane';
export const BACKEND_SWIM_LANE_QUERY_PARAM = 'swimLane';

export interface SwimLaneStorage {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
}

export interface SwimLaneRequestHeadersOptions {
    /** 覆盖浏览器查询字符串，主要供宿主集成和测试使用。 */
    search?: string;
    /** 覆盖 localStorage；传 null 可关闭持久化。 */
    storage?: SwimLaneStorage | null;
    /** 后端 swim-lane 的兜底值，用于兼容现有 Vite 环境行为。 */
    fallbackBackendSwimLane?: string | null;
    /** 前端 swim-lane 的可选兜底值。 */
    fallbackFrontendSwimLane?: string | null;
}

function readBrowserStorage(): SwimLaneStorage | null {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

function normalizeLane(value: string | null | undefined): string | null {
    const normalized = value?.trim();
    return normalized || null;
}

function readStoredLane(storage: SwimLaneStorage | null, key: string): string | null {
    if (!storage) {
        return null;
    }

    try {
        return normalizeLane(storage.getItem(key));
    } catch {
        return null;
    }
}

function persistLane(storage: SwimLaneStorage | null, key: string, value: string): void {
    if (!storage) {
        return;
    }

    try {
        storage.setItem(key, value);
    } catch {
        // 浏览器存储受限时不能影响 API 请求。
    }
}

function resolveLane(
    params: URLSearchParams,
    storage: SwimLaneStorage | null,
    queryParam: string,
    fallback: string | null | undefined
): string | null {
    const urlLane = normalizeLane(params.get(queryParam));
    if (urlLane) {
        persistLane(storage, queryParam, urlLane);
        return urlLane;
    }

    return readStoredLane(storage, queryParam) ?? normalizeLane(fallback);
}

/**
 * 解析本地开发用的 swim-lane 查询参数并转换成请求头。
 * 优先级为 URL 参数 > localStorage > fallback；浏览器存储受限时不影响请求。
 */
export function getSwimLaneRequestHeaders(
    options: SwimLaneRequestHeadersOptions = {}
): Record<string, string> {
    const search = options.search ?? (typeof window !== 'undefined' ? window.location.search : '');
    const storage = options.storage === undefined ? readBrowserStorage() : options.storage;
    const params = new URLSearchParams(search);
    const frontendSwimLane = resolveLane(
        params,
        storage,
        FRONTEND_SWIM_LANE_QUERY_PARAM,
        options.fallbackFrontendSwimLane
    );
    const backendSwimLane = resolveLane(
        params,
        storage,
        BACKEND_SWIM_LANE_QUERY_PARAM,
        options.fallbackBackendSwimLane
    );

    return {
        ...(frontendSwimLane ? { [FRONTEND_SWIM_LANE_HEADER]: frontendSwimLane } : {}),
        ...(backendSwimLane ? { [BACKEND_SWIM_LANE_HEADER]: backendSwimLane } : {}),
    };
}
